import type {
  AgentEvent,
  AgentEventType,
  AgentProgress,
  ExecutionScope,
  MiddlewareTodo,
  TodoItem,
  UIBlock,
} from '../types/agent.progress.js';

export function createInitialAgentProgressState(rootScopeId: string): AgentProgress {
  return {
    status: 'idle',
    blocks: {},
    rootTimeline: [],
    scopes: {},
    rootScopeId,
    todos: [],
    lastSequenceApplied: 0,
  };
}

/**
 * `blocks` and `scopes` are typed as Records, which index as always-present
 * (noUncheckedIndexedAccess is off). At runtime an id can still name a block or
 * scope that doesn't exist — e.g. a parentBlockId from another scope — so
 * lookups go through these to keep the type honest.
 */
function findBlock(state: AgentProgress, blockId: string): UIBlock | undefined {
  return state.blocks[blockId];
}

function findScope(state: AgentProgress, scopeId: string): ExecutionScope | undefined {
  return state.scopes[scopeId];
}

export function addBlock(state: AgentProgress, block: UIBlock): void {
  state.blocks[block.id] = block;
  state.activeBlockId = block.id;

  if (block.parentBlockId) {
    const parent = findBlock(state, block.parentBlockId);
    if (parent) {
      parent.childBlockIds.push(block.id);
      parent.updatedAt = block.updatedAt;
      return;
    }
  }

  state.rootTimeline.push(block.id);
}

export function updateBlock(
  state: AgentProgress,
  blockId: string,
  updater: (block: UIBlock) => void,
  timestamp: number
): void {
  const block = findBlock(state, blockId);
  if (!block) return;

  updater(block);
  block.updatedAt = timestamp;
}

export function upsertScope(state: AgentProgress, scope: ExecutionScope): void {
  state.scopes[scope.id] = scope;
}

export function updateScope(
  state: AgentProgress,
  scopeId: string,
  updater: (scope: ExecutionScope) => void
): void {
  const scope = findScope(state, scopeId);
  if (!scope) return;
  updater(scope);
}

function inferDepth(state: AgentProgress, parentBlockId: string | undefined): number {
  if (!parentBlockId) return 0;
  const parent = findBlock(state, parentBlockId);
  if (!parent) return 0;
  return parent.depth + 1;
}

function reconcileTodos(previous: TodoItem[], nextTodos: MiddlewareTodo[], timestamp: number): TodoItem[] {
  const previousMap = new Map(previous.map((todo) => [todo.key, todo]));
  const counts = new Map<string, number>();

  return nextTodos.map((todo) => {
    const count = (counts.get(todo.content) ?? 0) + 1;
    counts.set(todo.content, count);

    const key = `${todo.content}::${count}`;
    const existing = previousMap.get(key);

    return {
      key,
      content: todo.content,
      status: todo.status,
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
    };
  });
}

function markStepsCompleted(steps: { status: string }[]): void {
  for (const step of steps) {
    if (step.status === 'running') {
      step.status = 'completed';
    }
  }
}

function handleScopeStarted(next: AgentProgress, event: AgentEvent): void {
  const blockId = String(event.payload.blockId);
  const parentBlockId = event.parentBlockId;

  upsertScope(next, {
    id: event.scopeId,
    kind: String(event.payload.scopeKind) as any,
    name: String(event.payload.name),
    parentScopeId:
      typeof event.payload.parentScopeId === 'string' ? event.payload.parentScopeId : undefined,
    parentBlockId,
    startedAt: event.timestamp,
    status: 'running',
  });

  addBlock(next, {
    id: blockId,
    kind: 'scope',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    scopeId: event.scopeId,
    parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, parentBlockId),
    scopeKind: String(event.payload.scopeKind) as any,
    name: String(event.payload.name),
  });
}

function handleScopeCompleted(next: AgentProgress, event: AgentEvent): void {
  updateScope(next, event.scopeId, (scope) => {
    scope.status = 'completed';
    scope.completedAt = event.timestamp;
  });

  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'scope') {
        block.status = 'completed';
      }
    },
    event.timestamp
  );
}

function handleScopeFailed(next: AgentProgress, event: AgentEvent): void {
  updateScope(next, event.scopeId, (scope) => {
    scope.status = 'error';
    scope.completedAt = event.timestamp;
  });

  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'scope') {
        block.status = 'error';
      }
    },
    event.timestamp
  );

  next.status = 'error';
}

function handleThinkingStarted(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'thinking',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    summary: typeof event.payload.summary === 'string' ? event.payload.summary : undefined,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });

  next.status = 'thinking';
}

function handleThinkingUpdated(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'thinking') {
        block.summary = typeof event.payload.summary === 'string' ? event.payload.summary : block.summary;
        block.status = 'running';
      }
    },
    event.timestamp
  );

  next.status = 'thinking';
}

function handleThinkingCompleted(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'thinking') {
        block.status = 'completed';
      }
    },
    event.timestamp
  );
}

function handleThoughtStreamStarted(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'thought_stream',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    provider: typeof event.payload.provider === 'string' ? event.payload.provider : undefined,
    text: '',
    streaming: true,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });

  next.status = 'thinking';
}

function handleThoughtStreamDelta(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'thought_stream') {
        block.text += String(event.payload.textDelta ?? '');
        block.streaming = true;
        block.status = 'running';
      }
    },
    event.timestamp
  );

  next.status = 'thinking';
}

function handleThoughtStreamCompleted(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'thought_stream') {
        block.streaming = false;
        block.status = 'completed';
      }
    },
    event.timestamp
  );
}

function handleToolCallStarted(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'tool_call',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    toolCallId: String(event.payload.toolCallId),
    name: String(event.payload.name),
    input: event.payload.input,
    startedAt: event.timestamp,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });

  next.status = 'running_tool';
}

function handleToolCallCompleted(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'tool_call') {
        block.output = event.payload.output;
        block.completedAt = event.timestamp;
        block.status = 'completed';
      }
    },
    event.timestamp
  );

  next.status = 'thinking';
}

function handleToolCallFailed(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'tool_call') {
        block.output = event.payload.error;
        block.completedAt = event.timestamp;
        block.status = 'error';
      }
    },
    event.timestamp
  );

  next.status = 'error';
}

function handleMessageStarted(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'message',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    role: 'assistant',
    text: '',
    streaming: true,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });

  next.status = 'streaming';
}

function handleMessageDelta(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'message') {
        block.text += String(event.payload.textDelta ?? '');
        block.streaming = true;
        block.status = 'running';
      }
    },
    event.timestamp
  );

  next.status = 'streaming';
}

function handleMessageCompleted(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind === 'message') {
        block.text = typeof event.payload.fullText === 'string' ? event.payload.fullText : block.text;
        block.streaming = false;
        block.status = 'completed';
      }
    },
    event.timestamp
  );

  next.status = 'done';
}

function handleTaskStarted(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'task',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'running',
    steps: [
      {
        displayValue: String(event.payload.displayValue ?? ''),
        status: 'running',
      },
    ],
    startedAt: event.timestamp,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });
}

function handleTaskUpdated(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind !== 'task') return;
      markStepsCompleted(block.steps);
      if (typeof event.payload.displayValue === 'string') {
        block.steps.push({
          displayValue: event.payload.displayValue,
          status: 'running',
        });
      }
      block.status = 'running';
    },
    event.timestamp
  );
}

function handleTaskCompleted(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind !== 'task') return;
      markStepsCompleted(block.steps);
      if (typeof event.payload.displayValue === 'string') {
        block.steps.push({
          displayValue: event.payload.displayValue,
          status: 'completed',
        });
      }
      block.completedAt = event.timestamp;
      block.status = 'completed';
    },
    event.timestamp
  );
}

/**
 * Marks a task as failed without failing the query: a task is one unit of
 * work inside a run (e.g. an external service's pipeline phase), and the
 * agent may still recover and answer. Query-level failure stays with
 * `query_failed`.
 */
function handleTaskFailed(next: AgentProgress, event: AgentEvent): void {
  updateBlock(
    next,
    String(event.payload.blockId),
    (block) => {
      if (block.kind !== 'task') return;
      for (const step of block.steps) {
        if (step.status === 'running') {
          step.status = 'error';
        }
      }
      if (typeof event.payload.displayValue === 'string') {
        block.steps.push({
          displayValue: event.payload.displayValue,
          status: 'error',
        });
      }
      block.completedAt = event.timestamp;
      block.status = 'error';
    },
    event.timestamp
  );
}

function handleTodosReplaced(next: AgentProgress, event: AgentEvent): void {
  const todos = Array.isArray(event.payload.todos) ? (event.payload.todos as MiddlewareTodo[]) : [];
  next.todos = reconcileTodos(next.todos, todos, event.timestamp);
}

function handleQueryFailed(next: AgentProgress, event: AgentEvent): void {
  addBlock(next, {
    id: String(event.payload.blockId),
    kind: 'error',
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    status: 'error',
    message: String(event.payload.message ?? 'Query failed'),
    details: event.payload.details,
    scopeId: event.scopeId,
    parentBlockId: event.parentBlockId,
    childBlockIds: [],
    depth: inferDepth(next, event.parentBlockId),
  });

  next.status = 'error';
}

function handleQueryCompleted(next: AgentProgress, event: AgentEvent): void {
  next.status = 'done';
  if (typeof event.payload.summaryMetadata === 'string') {
    next.summaryMetadata = event.payload.summaryMetadata;
  }
}

function handleQueryCancelled(next: AgentProgress, event: AgentEvent): void {
  next.status = 'cancelled';
  next.cancelledAt = event.timestamp;

  for (const block of Object.values(next.blocks)) {
    if (block.status === 'running' || block.status === 'pending') {
      block.status = 'cancelled';
      block.updatedAt = event.timestamp;
    }
  }

  for (const scope of Object.values(next.scopes)) {
    if (scope.status === 'running') {
      scope.status = 'cancelled';
      scope.completedAt = event.timestamp;
    }
  }
}

type EventHandler = (next: AgentProgress, event: AgentEvent) => void;

// Keyed by AgentEventType so adding an event type without a handler fails to compile.
const eventHandlers: Record<AgentEventType, EventHandler> = {
  scope_started: handleScopeStarted,
  scope_completed: handleScopeCompleted,
  scope_failed: handleScopeFailed,
  thinking_started: handleThinkingStarted,
  thinking_updated: handleThinkingUpdated,
  thinking_completed: handleThinkingCompleted,
  thought_stream_started: handleThoughtStreamStarted,
  thought_stream_delta: handleThoughtStreamDelta,
  thought_stream_completed: handleThoughtStreamCompleted,
  tool_call_started: handleToolCallStarted,
  tool_call_completed: handleToolCallCompleted,
  tool_call_failed: handleToolCallFailed,
  message_started: handleMessageStarted,
  message_delta: handleMessageDelta,
  message_completed: handleMessageCompleted,
  task_started: handleTaskStarted,
  task_updated: handleTaskUpdated,
  task_completed: handleTaskCompleted,
  task_failed: handleTaskFailed,
  todos_replaced: handleTodosReplaced,
  query_failed: handleQueryFailed,
  query_completed: handleQueryCompleted,
  query_cancelled: handleQueryCancelled,
};

/** Persisted or socket-delivered events can carry a type this build doesn't know. */
function findEventHandler(type: AgentEventType): EventHandler | undefined {
  return eventHandlers[type];
}

export function reduceAgentEvent(state: AgentProgress, event: AgentEvent): AgentProgress {
  const next = structuredClone(state);
  const handler = findEventHandler(event.type);
  if (handler) {
    handler(next, event);
  }
  next.lastSequenceApplied = event.sequence;
  return next;
}
